/**
 * Auto-route A&R scouts (role='ar') from creator.{tenant} and
 * labels.{tenant} to scouts.{tenant}.
 *
 * Mirrors labels-redirect.global.ts. Triggered on every navigation
 * (not just opt-in pages) because a scout typing a creator/labels URL,
 * arriving via a bookmark, or losing a post-login race should be put
 * back on their own panel before any page renders.
 *
 * Scope:
 *   - URL hostname starts with `creator.` OR `labels.`
 *   - User is logged in
 *   - User role is exactly 'ar' (admins and other tiers stay where
 *     they are; admins can preview scouts.* explicitly via the URL)
 *
 * Path is NOT preserved — scouts.* has its own page tree (router.options.ts
 * strips /scouts/), so a path like /distribution/releases wouldn't
 * exist on the scout subdomain. Drop the user on the scout root and
 * let their sidebar take them where they need.
 */
export default defineNuxtRouteMiddleware((to) => {
    const url = useRequestURL()
    // Dev-only, and only once this middleware is on a host it could act on —
    // see the matching note in labels-redirect.global.ts. Announcing every
    // navigation before the host check made four lines per page on panels
    // where this can never fire.
    const debug = import.meta.dev
    const where = typeof window === 'undefined' ? 'ssr' : 'csr'

    const onCreator = url.hostname.startsWith('creator.')
    const onLabels = url.hostname.startsWith('labels.')
    // The bare apex (no `creator.` / `labels.` / `scouts.` / `admin.`
    // prefix) keeps /scouts/* in its route tree so legacy invite emails
    // — which point at `${domain}/scouts` — still land on a real page.
    // A logged-in scout reaching that page belongs on scouts.{tenant};
    // catch them here and forward.
    const stripped = to.path.replace(/^\/(en|de|pt|es)(?=\/|$)/, '')
    const onApexScoutsPath = !onCreator && !onLabels
        && !url.hostname.startsWith('scouts.')
        && !url.hostname.startsWith('admin.')
        && (stripped === '/scouts' || stripped.startsWith('/scouts/'))

    if (!onCreator && !onLabels && !onApexScoutsPath) return

    const { user } = useAuth()
    const u = user.value
    if (!u) return

    if (u.role !== 'ar') {
        // Kept: on a host this middleware governs, "why did this NOT redirect"
        // is a real question. The lines above answered one nobody asked.
        if (debug) console.log(`[scouts-redirect ${where}] staying: role=${u.role}`)
        return
    }

    // For apex /scouts/* we strip the prefix so the same path resolves
    // on scouts.{tenant} (router.options.ts strips /scouts/ there).
    // For creator/labels we just go to root — those paths have no
    // counterpart on the scout panel.
    const targetPath = onApexScoutsPath
        ? (stripped.replace(/^\/scouts/, '') || '/')
        : '/'

    const mainDomain = (onCreator || onLabels)
        ? url.hostname.replace(/^(creator|labels)\./, '')
        : url.hostname
    const protocol = url.protocol
    const port = url.port ? `:${url.port}` : ''
    const target = `${protocol}//scouts.${mainDomain}${port}${targetPath}`
    if (debug) console.log(`[scouts-redirect ${where}] REDIRECTING to ${target}`)
    return navigateTo(target, { external: true, replace: true })
})
